import type React from "react";
import { useCallback, useEffect, useRef } from "react";

type TabNavigationKey =
	| "ArrowLeft"
	| "ArrowUp"
	| "ArrowRight"
	| "ArrowDown"
	| "Home"
	| "End";

interface UseTablistNavigationOptions<T extends string> {
	tabs: readonly T[];
	activeTab: T;
	onChange: (tab: T) => void;
}

interface UseTablistNavigationResult<T extends string> {
	getTabProps: (tab: T) => {
		ref: (node: HTMLButtonElement | null) => void;
		tabIndex: number;
		"aria-selected": boolean;
		onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
	};
}

export const useTablistNavigation = <T extends string>({
	tabs,
	activeTab,
	onChange,
}: UseTablistNavigationOptions<T>): UseTablistNavigationResult<T> => {
	const tabRefs = useRef<Record<T, HTMLButtonElement | null>>(
		{} as Record<T, HTMLButtonElement | null>,
	);
	const shouldMoveFocusRef = useRef(false);

	const moveToTab = useCallback(
		(key: TabNavigationKey) => {
			const currentIndex = tabs.indexOf(activeTab);
			if (currentIndex === -1) return;

			if (key === "Home") {
				onChange(tabs[0]);
				return;
			}

			if (key === "End") {
				onChange(tabs[tabs.length - 1]);
				return;
			}

			const direction = key === "ArrowLeft" || key === "ArrowUp" ? -1 : 1;
			const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
			onChange(tabs[nextIndex]);
		},
		[activeTab, onChange, tabs],
	);

	useEffect(() => {
		const activeElement = document.activeElement;
		const tabElements = Object.values(tabRefs.current).filter(
			(element): element is HTMLButtonElement => element !== null,
		);
		const isFocusInsideTablist = tabElements.some(
			(element) => element === activeElement,
		);

		if (!shouldMoveFocusRef.current && !isFocusInsideTablist) {
			return;
		}

		tabRefs.current[activeTab]?.focus();
		shouldMoveFocusRef.current = false;
	}, [activeTab]);

	const getTabProps = useCallback(
		(tab: T) => ({
			ref: (node: HTMLButtonElement | null) => {
				tabRefs.current[tab] = node;
			},
			tabIndex: activeTab === tab ? 0 : -1,
			"aria-selected": activeTab === tab,
			onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => {
				switch (event.key) {
					case "ArrowLeft":
					case "ArrowUp":
					case "ArrowRight":
					case "ArrowDown":
					case "Home":
					case "End":
						event.preventDefault();
						shouldMoveFocusRef.current = true;
						moveToTab(event.key);
						break;
					case "Enter":
					case " ":
						event.preventDefault();
						onChange(tab);
						break;
					default:
						break;
				}
			},
		}),
		[activeTab, moveToTab, onChange],
	);

	return { getTabProps };
};
